import * as THREE from 'three';
import type { SceneGraph, SceneNode } from '../geometry';
import {
  BASE_WRAP,
  FABRIC_THICKNESS,
  chainPoint,
  fabricPointAt,
  windingAngle,
  rollState,
} from '../geometry/animation/rolloMotion';
import { resolveMaterial } from './materials';

interface Strip {
  mesh: THREE.Mesh<THREE.BufferGeometry>;
  samples: number[];
  width: number;
  angles: number[];
}

function makeStrip(width: number, from: number, to: number, materialId?: string): Strip {
  // ≤2 мм вдоль ткани: витки не превращаются в многоугольник на крупном плане.
  const count = Math.max(2, Math.ceil((to - from) / 0.002));
  const samples = Array.from({ length: count + 1 }, (_, i) => from + ((to - from) * i) / count);
  const positions = new Float32Array(samples.length * 4 * 3);
  const uv = new Float32Array(samples.length * 4 * 2);
  const indices: number[] = [];
  samples.forEach((s, i) => {
    for (let j = 0; j < 4; j++) {
      uv[(i * 4 + j) * 2] = j % 2 === 0 ? 0 : width;
      uv[(i * 4 + j) * 2 + 1] = to - s;
    }
    if (i === count) return;
    const b = i * 4;
    for (const face of [
      [0, 1],
      [3, 2],
      [2, 0],
      [1, 3],
    ]) {
      const [a, c] = face as [number, number];
      indices.push(b + a, b + a + 4, b + c, b + c, b + a + 4, b + c + 4);
    }
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geometry.setIndex(indices);
  const mesh = new THREE.Mesh(geometry, resolveMaterial(materialId));
  mesh.frustumCulled = false;
  return {
    mesh,
    samples,
    width,
    angles: samples.map((s) => windingAngle(Math.max(0, BASE_WRAP + s))),
  };
}

export interface RolloAnimator {
  setProgress(value: number): void;
  dispose(): void;
}

/** Деформирует ткань и перемещает рейки; неподвижные крепления/кассета остаются на месте. */
export function createRolloAnimation(model: THREE.Group, graph: SceneGraph): RolloAnimator {
  const updates: Array<(progress: number) => void> = [];
  const owned: Array<THREE.BufferGeometry> = [];
  const visit = (node: SceneNode) => {
    if (node.tags?.includes('layer')) {
      const top = node.children.find((n) => n.tags?.includes('top'));
      const sections = node.children.filter((n) => n.tags?.includes('section'));
      if (!top || !sections.length) return;
      const height = sections.reduce((h, n) => h + n.transform.scale[1], 0);
      const centerY = top.transform.position[1];
      const parent = model.getObjectByName(node.id)!;
      const strips: Strip[] = [];
      for (const section of sections) {
        const original = model.getObjectByName(section.id)!;
        original.visible = false;
        const h = section.transform.scale[1];
        const from = height - section.transform.position[1] - h / 2;
        const strip = makeStrip(section.transform.scale[0], from, from + h, section.materialId);
        strip.mesh.name = `animated.${section.id}`;
        strip.mesh.position.y = centerY;
        parent.add(strip.mesh);
        strips.push(strip);
      }
      const upper = sections[sections.length - 1]!;
      const base = makeStrip(upper.transform.scale[0], -BASE_WRAP, 0, upper.materialId);
      base.mesh.position.y = centerY;
      parent.add(base.mesh);
      strips.push(base);
      owned.push(...strips.map((s) => s.mesh.geometry));
      const rails = node.children
        .filter((n) => n.tags?.includes('rail'))
        .map((n) => ({
          object: model.getObjectByName(n.id)!,
          distance: height - n.transform.position[1],
          initialRotation: n.transform.rotation[0],
          bottom: n.tags?.includes('bottombar'),
        }));
      const tube = model.getObjectByName(top.id)!;
      const chain = node.children.find((n) => n.id.startsWith('chain.'));
      let beads: THREE.InstancedMesh | undefined;
      let sprocket: THREE.Object3D | undefined;
      let drop = 0;
      const loopRadius = 0.02;
      let perimeter = 0;
      if (chain) {
        const front = chain.children.find((n) => n.shape === 'beadChain')!;
        const sprocketNode = chain.children.find((n) => n.tags?.includes('sprocket'))!;
        sprocket = model.getObjectByName(sprocketNode.id)!;
        for (const n of chain.children.filter((n) => n.shape === 'beadChain'))
          model.getObjectByName(n.id)!.visible = false;
        drop = front.transform.scale[1];
        perimeter = 2 * drop + 2 * Math.PI * loopRadius;
        const geometry = new THREE.SphereGeometry(0.002, 10, 8);
        owned.push(geometry);
        beads = new THREE.InstancedMesh(
          geometry,
          resolveMaterial(front.materialId),
          Math.round(perimeter / 0.0045),
        );
        beads.name = `animated.${chain.id}`;
        beads.position.set(sprocketNode.transform.position[0], centerY, 0);
        beads.frustumCulled = false;
        parent.add(beads);
      }
      const matrix = new THREE.Matrix4();
      updates.push((progress) => {
        const state = rollState(height, progress);
        tube.rotation.x = state.angle - 2 * Math.PI;
        if (sprocket) sprocket.rotation.x = tube.rotation.x;
        for (const strip of strips) {
          const positions = strip.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
          strip.samples.forEach((s, i) => {
            const point = fabricPointAt(s, state, strip.angles[i]);
            for (let j = 0; j < 4; j++) {
              const offset = ((j < 2 ? 1 : -1) * FABRIC_THICKNESS) / 2;
              positions.setXYZ(
                i * 4 + j,
                ((j % 2 === 0 ? -1 : 1) * strip.width) / 2,
                point.y + offset * Math.sin(point.rotation),
                point.z + offset * Math.cos(point.rotation),
              );
            }
          });
          positions.needsUpdate = true;
          strip.mesh.geometry.computeVertexNormals();
        }
        for (const rail of rails) {
          const point = fabricPointAt(rail.distance, state);
          rail.object.position.y = centerY + point.y - (rail.bottom ? 0.015 : 0);
          rail.object.position.z = point.z;
          rail.object.rotation.x = rail.initialRotation + (rail.bottom ? 0 : point.rotation);
        }
        if (beads) {
          const travel = (state.angle - 2 * Math.PI) * loopRadius;
          for (let i = 0; i < beads.count; i++) {
            const p = chainPoint((i * perimeter) / beads.count + travel, drop, loopRadius);
            matrix.makeTranslation(0, p.y, p.z);
            beads.setMatrixAt(i, matrix);
          }
          beads.instanceMatrix.needsUpdate = true;
        }
      });
    }
    for (const child of node.children) visit(child);
  };
  visit(graph.root);
  const animator = {
    setProgress: (value: number) =>
      updates.forEach((update) => update(Math.min(1, Math.max(0, value)))),
    dispose: () => owned.forEach((geometry) => geometry.dispose()),
  };
  animator.setProgress(0);
  return animator;
}
